import axios from "axios";
import { getAuthHeaders } from "../Api";

// Interfaces
export interface AccountSettings {
  _id: string;
  accountId: string;
  domains?: string[];
}

export interface PIIConfiguration {
  type: 'configuration';
  name: 'pii-redaction';
  isEnabled: boolean;
  selectedTypes: string[];
  excludedDomains: string[];
  updatedAt: Date;
  createdAt: Date;
}

export interface MLSettings {
  businessCategoryDaysWindow: { [businessCategoryKey: string]: number };
}

export interface PACModelConfig {
  _id: {
    $oid: string;
  };
  name: string;
  account_id: {
    $oid: string;
  };
  auto_recalc: boolean;
  target_model: {
    model_name: string;
    model_version: string;
    [key: string]: any;
  };
  tuning_needed: boolean;
  updated_at: string;
  last_completed_recalc: {
    finished_at: string;
    model: {
      model_name: string;
      model_version: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

export interface IgnoreAccountIdsConfiguration {
  isEnabled: boolean;
  accountIds: string[];
}

export interface TokenDocument {
  _id: string;
  token: string;
  name: string;
  createdAt: string;
  username: string;
}

// Dev Team Interface
export interface DevTeam {
  _id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// User Invitation
export interface UserInvite {
  name: string;
  email: string;
  role?: string;
  team?: string;
}

export interface InviteOptions {
  defaultRole?: string;
  sendInvite?: boolean;
}

const API_BASE_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/settings";

// Account Settings API
export const getAccountSettings = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/`, config);
    return response.data.account;
  } catch (e) {
    console.error("Error fetching account settings", e);
    throw e;
  }
};

export const updateAccountSettings = async (settings: Partial<AccountSettings>, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_BASE_URL}/`,
      { settings },
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating account settings", e);
    throw e;
  }
};

export const updateDomains = async (domain: string, push: boolean, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_BASE_URL}/domains`,
      { data: { domain, push } },
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating domains", e);
    throw e;
  }
};

// API Tokens
export const createToken = async (type: string, name: string, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_BASE_URL}/token/${type}`,
      { name },
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error creating token", e);
    throw e;
  }
};

export const getTokens = async (type: string, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/token/${type}`, config);
    return response.data.tokenDoc;
  } catch (e) {
    console.error("Error fetching tokens", e);
    throw e;
  }
};

export const deleteToken = async (id: string, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.delete(`${API_BASE_URL}/token/${id}`, config);
    return response.data;
  } catch (e) {
    console.error("Error deleting token", e);
    throw e;
  }
};

// PII Redaction
export const getPIIConfiguration = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/pii-redaction`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching PII configuration", e);
    throw e;
  }
};

export const updatePIIConfiguration = async (
  update: {
    isEnabled: boolean;
    selectedTypes: string[];
    excludedDomains: string[];
  },
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_BASE_URL}/pii-redaction`,
      update,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating PII configuration", e);
    throw e;
  }
};

// Dev Teams API
export const getDevTeams = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/dev-teams`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching dev teams", e);
    throw e;
  }
};

export const getDevTeam = async (id: string, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/dev-teams/${id}`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching dev team", e);
    throw e;
  }
};

export const createDevTeam = async (
  team: { name: string; description?: string; ownerId: string; ownerName: string },
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_BASE_URL}/dev-teams`,
      team,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error creating dev team", e);
    throw e;
  }
};

export const updateDevTeam = async (
  id: string,
  update: { name?: string; description?: string; ownerId?:string },
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_BASE_URL}/dev-teams/${id}`,
      update,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating dev team", e);
    throw e;
  }
};

export const deleteDevTeam = async (id: string, auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.delete(`${API_BASE_URL}/dev-teams/${id}`, config);
    return response.data;
  } catch (e) {
    console.error("Error deleting dev team", e);
    throw e;
  }
};

// User Invitation
export const inviteBulkUsers = async (
  users: UserInvite[],
  options: InviteOptions = {},
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.post(
      `${API_BASE_URL}/invite-users`,
      { users, options },
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error inviting users", e);
    throw e;
  }
}; 


// Bagel Objects
export const getBagelObjects = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/bagel-objects`, config);
    return response.data.bagelObjects;
  } catch (e) {
    console.error("Error fetching Bagel objects", e);
    throw e;
  }
};

// Ignore Account IDs
export const getIgnoreAccountIds = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/ignore-account-ids`, config);
    return response.data.config;
  } catch (e) {
    console.error("Error fetching Ignore Account IDs configuration", e);
    throw e;
  }
};

export const updateIgnoreAccountIds = async (
  update: {
    isEnabled?: boolean;
    accountIds?: string[];
  },
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_BASE_URL}/ignore-account-ids`,
      update,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating Ignore Account IDs configuration", e);
    throw e;
  }
};

// ML Settings
export const getMLSettings = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/ml`, config);
    return response.data.config;
  } catch (e) {
    console.error("Error fetching ML settings", e);
    throw e;
  }
};

export const updateMLSettings = async (
  update: {
    businessCategoryDaysWindow: { [businessCategoryKey: string]: number };
  },
  auth0: any
) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.put(
      `${API_BASE_URL}/ml`,
      update,
      config
    );
    return response.data;
  } catch (e) {
    console.error("Error updating ML settings", e);
    throw e;
  }
};

// PACModelConfig Settings
export const getPACModelConfig = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.get(`${API_BASE_URL}/ml/PACModelConfig`, config);
    return response.data;
  } catch (e) {
    console.error("Error fetching PACModelConfig", e);
    throw e;
  }
};

export const triggerPACModelRecalculation = async (auth0: any) => {
  try {
    const config = await getAuthHeaders(auth0);
    const response = await axios.put(`${API_BASE_URL}/ml/recalc-all-product-areas`, {}, config);
    return response.data;
  } catch (e) {
    console.error("Error triggering PACModelConfig recalculation", e);
    throw e;
  }
};

